import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Feather, Pencil } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArticleCard } from "@/components/ArticleCard";
import { FrameCorners, KnotDivider } from "@/components/ornaments";
import { ProfileDialog } from "@/components/ProfileDialog";
import { PageState } from "@/pages/PageState";
import { useT } from "@/lib/labels";

const Avatar = ({ src, name }) => (
  <div data-testid="profile-avatar-image" className="w-36 h-36 shrink-0 border-2 border-[#c9a227] bg-[#ebe2cd] grid place-items-center overflow-hidden shadow-[4px_4px_0_#c9a227]">
    {src ? <img src={imgUrl(src)} alt={name} className="w-full h-full object-cover" /> : <span className="font-display text-6xl text-[#991b1b]">{name?.charAt(0)}</span>}
  </div>
);

export default function ReaderProfilePage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(undefined);
  const [edit, setEdit] = useState(false);
  const t = useT();
  const load = useCallback(() => api.get(`/readers/${slug}`).then((r) => setP(r.data)).catch(() => setP(null)), [slug]);
  useEffect(() => { setP(undefined); load(); }, [load]);
  useEffect(() => { if (p?.name) document.title = `${p.name} · Reader · Lorekeepers of Elirios`; }, [p]);
  if (!p) return <PageState loading={p === undefined} />;
  const own = user && user.profile_slug === slug;
  const joined = p.joined ? new Date(p.joined).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : "";

  return (
    <div data-testid="reader-profile-page" className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
      <section className="relative vellum gilt p-8 sm:p-10 rise flex flex-col sm:flex-row gap-8 items-start">
        <FrameCorners />
        <Avatar src={p.avatar_url} name={p.name} />
        <div className="flex-1">
          {t("profile.eyebrow") && <p className="eyebrow">{t("profile.eyebrow")}</p>}
          <h1 data-testid="profile-display-name" className="font-display text-4xl sm:text-5xl mt-1">{p.name}</h1>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 font-heading text-sm text-[#5e4a35]">
            {joined && <span data-testid="profile-joined">Joined {joined}</span>}
            <span data-testid="profile-contribution-count" className="inline-flex items-center gap-1"><Feather className="w-3.5 h-3.5" /> {p.contribution_count} approved contribution{p.contribution_count === 1 ? "" : "s"}</span>
          </div>
          {p.bio && <p data-testid="profile-bio-text" className="mt-5 text-lg italic text-[#4a3a2a] max-w-3xl whitespace-pre-line">{p.bio}</p>}
          {own && <button data-testid="profile-edit-button" onClick={() => setEdit(true)} className="btn-ghost-illum mt-6"><Pencil className="w-4 h-4" /> {t("profile.edit")}</button>}
        </div>
      </section>
      <KnotDivider className="my-12" />
      {t("profile.list_eyebrow") && <p className="eyebrow">{t("profile.list_eyebrow")}</p>}
      {t("profile.list_title") && <h2 className="font-display text-3xl mt-1 mb-8">{t("profile.list_title")}</h2>}
      {p.articles.length
        ? <div data-testid="profile-articles" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{p.articles.map((a, i) => <ArticleCard key={a.slug} a={a} index={i} />)}</div>
        : <p data-testid="profile-empty" className="vellum p-8 text-center italic">No approved contributions yet.</p>}
      {own && edit && <ProfileDialog open={edit} onOpenChange={setEdit} onSaved={load} />}
    </div>
  );
}
